import {Injectable} from '@angular/core';
import {DatePipe} from '@angular/common';
import {ActivatedRoute, Router} from '@angular/router';
import {EMPTY, of, throwError} from 'rxjs';
import {catchError, filter, map, shareReplay, switchMap, withLatestFrom} from 'rxjs/operators';

import {LpAssetDataService} from 'src/app/services/lp/lp-asset-data.service';
import {TerraUtils} from 'src/app/shared/TerraUtils';
import {AppSettingsService} from 'src/app/services/app-settings.service';
import {LpAssetReqRes} from '../LpAssetReqRes.model';
import {HoldingReportListItemReqRes} from 'src/app/shared/models/HoldingReportListItemReqRes.model';
import HoldingType from 'src/app/shared/enums/HoldingType.enum';
import AreaUnit from 'src/app/shared/enums/AreaUnit.enum';
import InvestmentType from 'src/app/shared/enums/InvestmentType.enum';
import {HoldingFileReqRes} from 'src/app/dashboard/shared/holding/HoldingFileReqRes.model';
import MetaFileLink from 'src/app/models/metaFileLink.model';
import HoldingFileType from 'src/app/shared/enums/HoldingFileType.enum';
import {LpHoldingService} from '../../shared/holdings/lp-holding.service';
import HTTP_STATUS_CODES from 'src/app/shared/enums/HttpStatusCodesEnum';
import {RoutingService} from 'src/app/services/routing.service';
import {StorageObjectDataService} from 'src/app/services/shared/storage-object-data.service';
import HoldingDiscriminator from 'src/app/shared/enums/HoldingDiscriminator.enum';
import {LpFundDataService} from 'src/app/services/lp/lp-fund-data.service';
import {TerraNumberPipe} from 'src/app/shared/pipes/TerraNumber.pipe';
import {LpHoldingDataService} from 'src/app/services/lp/lp-holding-data.service';
import { LoggerService } from 'src/app/shared/errors/logger.service';
import { AlertDialogParams } from 'src/app/shared/components/alert-dialog/alert-dialog.component';
import { DialogService } from 'src/app/services/dialog.service';
import { UserService } from 'src/app/services/shared/user.service';
import { LpInvestingEntityDataService } from 'src/app/services/lp/lp-investing-entity-data.service';

@Injectable()
export class LpAssetService extends LpHoldingService {
  readonly holdingDiscriminator = HoldingDiscriminator.Asset;

  reportId$ = this.route.queryParams.pipe(
    map(params => {
      return +params.reportId || null;
    }),
    shareReplay(1)
  );

  assetDetails$ = this.holdingId$.pipe(
    withLatestFrom(this.userService.accountDetails$),
    filter(([id,accountDetails])  => this.checkSameEmails(accountDetails.userName, HoldingDiscriminator.Asset)),
    map(([id,accountDetails]) => id),
    switchMap(assetId => assetId === TerraUtils.consts.EXAMPLE_ASSET_ID ? of(this.getExampleAsset()) : this.lpAssetDataService.getById(assetId)),
    catchError(err => {
      if (err.status == HTTP_STATUS_CODES.NOT_FOUND) {
        this.router.navigateByUrl(this.routingService.lpHomePage);
      }
      return throwError(err)
    }),
    shareReplay(1)
  );

  holdingDetails$ = this.assetDetails$;

  assetReportsBasic$ = this.assetDetails$.pipe(
    map(asset => asset.reports),
    map(reports => reports.sort((a, b) => {
      if (a.periodStartDate === b.periodStartDate) {
        return 0;
      } else if (a.periodStartDate >= b.periodStartDate) {
        return -1;
      }
      return 1;
    })
    )
  );

  transactions$ = this.assetDetails$.pipe(map(asset => asset.transactions));

  status$ = this.assetDetails$.pipe(map(asset => asset.status));

  constructor(
    private storageObjectDataService: StorageObjectDataService,
    private lpAssetDataService: LpAssetDataService,
    private lpFundDataService: LpFundDataService,
    private appSettingsService: AppSettingsService,
    private route: ActivatedRoute,
    private router: Router,
    private routingService: RoutingService,
    private datePipe: DatePipe,
    private terraNumberPipe: TerraNumberPipe,
    private lpHoldingDataService: LpHoldingDataService,
    private logger: LoggerService,
    private dialogService:DialogService,
    private userService:UserService,
    private lpInvestingEntityDataService:LpInvestingEntityDataService
  ) {
    super(
      storageObjectDataService, routingService, router, route, lpFundDataService,
      datePipe, terraNumberPipe, lpHoldingDataService, logger, userService, dialogService,lpInvestingEntityDataService);
  }

  getAssetcurrencyOrParentFundCurrency(asset: LpAssetReqRes) {
    if (!asset) {
      return null;
    }
    if (asset.initialCurrency) {
      return asset.initialCurrency;
    }
    else if (asset.fund) {
      return asset.fund.initialCurrency;
    }
    return
  }

  private getExampleAsset(): LpAssetReqRes {

    var assetDetails = {
      id: TerraUtils.consts.EXAMPLE_ASSET_ID,
      isExampleAsset: true,
      discriminator: HoldingDiscriminator.Asset,
      gpDetails: {
        agentCompanyName: 'Mott Real Estate'
      },
      name: '143 Mott st.',
      initialCurrency: {
        id: 4,
        iso: 'USD',
        name: 'United States Dollar',
        symbol: '$'
      },
      investmentType: InvestmentType.Core,
      finalClosingDate: new Date(new Date().getFullYear(), new Date().getMonth() - 3, new Date().getDay(), 13, 0),
      totalCapitalization: 5500000,
      totalInvestorEquity: 4000000,
      gpEquityFromTotalEquity: 15,
      totalEquityInvestedToDate: 5500000,
      totalReturnsToDate: 50000,
      estimatedMarketValue: 5600000,
      holdingType: HoldingType.MultiFamily,
      areaUnits: AreaUnit.SquareFeet,
      lotSizeArea: 4000,
      stories: 3,
      residentialUnits: 5,
      retailUnits: 2,
      grossBuildingArea: 3700,
      residentialNetSellableArea: 2300,
      retailNetSellableArea: 400,
      lenderInformation: {
        amount: 1500000,
        lenderName: 'Wells Fargo',
        loanToValue: 27,
        interestRate: 1.2,
      },
      location: {
        stateName: 'New York',
        cityName: 'New York',
        postalCode: '10001',
        countryId: 1
      },
      cumulativeProjectedPerformance: {
        targetInvestmentPeriod: 5,
        cashOnCashRate: 10.2,
        nonLeveragedCapRate: 7.6,
        targetEquityMultiplier: 1.7
      },
      position: [{
        totalInvested: 500000,
        principalBalance: 300000,
        returnToDate: 200000,
        cashOnCashRatePercentage: (200000 / 500000) * 100,
        distributionsCurrency: {
          symbol: '$',
          iso: 'USD'
        },
        contributionsCurrency: {
          symbol: '$',
          iso: 'USD'
        },
      }]
    } as LpAssetReqRes;

    assetDetails.reports = new Array<HoldingReportListItemReqRes>();

    const report1 = new HoldingReportListItemReqRes();
    report1.periodStartDate = new Date(assetDetails.finalClosingDate.getFullYear(), 0, 1, 13, 0);
    report1.periodEndDate = new Date(assetDetails.finalClosingDate.getFullYear(), 2, 31, 13, 0);
    report1.publishDate = report1.periodEndDate;
    report1.id = TerraUtils.consts.EXAMPLE_ASSET_ID;
    assetDetails.reports.push(report1);

    // Add attachments:
    assetDetails.attachments = new Array<HoldingFileReqRes>();

    const attachment = new HoldingFileReqRes();
    attachment.metaFileLink = new MetaFileLink();
    attachment.fileType = HoldingFileType.Photo;
    attachment.metaFileLink.url = this.appSettingsService.blobStorageUrlCDN + 'assets/images/example-asset-image-1.jpg';
    attachment.metaFileLink.mediaType = 'image/jpeg';
    assetDetails.attachments.push(attachment);

    const attachment2 = new HoldingFileReqRes();
    attachment2.metaFileLink = new MetaFileLink();
    attachment2.fileType = HoldingFileType.Photo;
    attachment2.metaFileLink.url = this.appSettingsService.blobStorageUrlCDN + 'assets/images/example-asset-image-2.jpg';
    attachment2.metaFileLink.mediaType = 'image/jpeg';
    assetDetails.attachments.push(attachment2);

    return assetDetails;
  }
}
